import { Component, OnDestroy } from '@angular/core';
import { ActivatedRoute } from "@angular/router";
import { AlbaranService } from "../../../services/albaran.service";
import { InfoAlbaran } from "../../../models/infoAlbaran";
import { global } from "../../../services/global";
import { AuthService} from "../../../services/auth.service";
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpResponse } from '@angular/common/http';
import { DecaCacheService } from "../../../services/deca-cache.service";
import { ConnectionService } from "../../../services/connection.service";
import { DataCacheService } from "../../../services/data-cache.service";
import { Subscription } from "rxjs";


@Component({
    selector: 'app-albaran-content',
    templateUrl: './albaran-content.component.html',
    styleUrls: ['./albaran-content.component.scss'],
    standalone: false
})
export class AlbaranContentComponent implements OnDestroy {

    showAlert: boolean;
    messageAlert :string;

    name: string = "";
    url = global.getUrl();

    infoAlbara: InfoAlbaran = new InfoAlbaran();

    numeroRuta: number = 0;
    idTransport: any;
    idAlbara: any;
    token : string;
    showLoader: boolean = false;

    // DeCA vigente del albarán: URL pública del PDF, o el motivo por el que no hay ninguno
    decaUrl: string | null = null;
    decaError: string | null = null;
    // Aviso cuando los datos del albarán vienen de la copia guardada
    infoBackupNotice: string | null = null;
    private restoredSubscription?: Subscription;
    // Sin conexión y con copia guardada del PDF: el botón descarga la copia, con un aviso debajo
    decaFromBackup: boolean = false;
    decaBackupNotice: string | null = null;

    constructor(
        private route: ActivatedRoute,
        private albaranService: AlbaranService,
        private auth: AuthService,
        private http: HttpClient,
        private decaCache: DecaCacheService,
        public connection: ConnectionService,
        private dataCache: DataCacheService,
    ) {
        this.route.params.subscribe(
            (params) => {
                this.idAlbara = params['idAlbara'];
                this.idTransport = params['idTransport'] || null; // Manejo del parámetro opcional

            }
        );
        this.token = this.auth.getToken();
        this.showAlert = false;
        this.messageAlert = '';
    }

    ngOnInit(): void {
        // Al recuperar la conexión se vuelven a pedir los datos, que hasta ahora eran copia
        this.restoredSubscription?.unsubscribe();
        this.restoredSubscription = this.connection.restored.subscribe(() => this.ngOnInit());

        this.showLoader = true;

        this.albaranService.getInfoAlbaran(this.idAlbara).subscribe(
            {
                next: infoAlbaran => {
                    this.applyInfo(infoAlbaran);
                    this.infoBackupNotice = null;

                    // Copia local para poder consultar el albarán sin cobertura
                    this.dataCache.saveAlbaranInfo(this.idAlbara, infoAlbaran);

                    this.connection.reportSuccess();
                    this.showLoader = false;
                }, error: error => {
                    // Sin esto el loader se quedaba girando para siempre
                    this.showLoader = false;

                    if (!this.connection.reportError(error)) {
                        this.showAlert = true;
                        this.messageAlert = error.error?.message ?? "No se ha podido cargar el albarán.";
                        return;
                    }

                    // Sin cobertura: la última copia guardada de este albarán, si la hay
                    const backup = this.dataCache.getAlbaranInfo(this.idAlbara);

                    if (backup) {
                        this.applyInfo(backup.info);
                        this.infoBackupNotice = 'Sin conexión: datos de la copia guardada '
                            + this.dataCache.describe(backup) + '.';
                    } else {
                        this.infoBackupNotice = 'Sin conexión y sin copia guardada de este albarán.';
                    }
                }
            }
        )

        this.getDeca();
    }

    ngOnDestroy(): void {
        this.restoredSubscription?.unsubscribe();
    }

    // Vuelca en la pantalla la info del albarán, venga del servidor o de la copia guardada.
    applyInfo(infoAlbaran: any): void {
        this.infoAlbara = infoAlbaran;
        this.name = "Albarán - " + this.infoAlbara.reference;
        this.infoAlbara.rutesADR = infoAlbaran.rutasADR;
        this.numeroRuta = 1;

        this.albaranService.modeTransport = infoAlbaran.modeTransport;
        this.albaranService.tipusTransport = infoAlbaran.tipusTransport;
        this.albaranService.idRecollidaEntregaContenidor = infoAlbaran.idRecollidaEntregaContenidor;
    }

    getDeca(): void {
        this.albaranService.getDeca(this.idAlbara).subscribe(
            {
                next: (deca: any) => {
                    this.decaUrl = deca.url ?? null;
                    this.decaError = null;
                    this.decaFromBackup = false;
                    this.decaBackupNotice = null;
                }, error: async (error: HttpErrorResponse) => {
                    this.decaUrl = null;

                    // Sin respuesta del servidor (sin cobertura): si hay copia del PDF, ofrecerla
                    if (this.connection.reportError(error)) {
                        const copy = await this.decaCache.getPdf(Number(this.idAlbara));

                        if (copy) {
                            this.decaFromBackup = true;
                            this.decaError = null;
                            this.decaBackupNotice = 'Sin conexión: copia del DeCA ' + this.dataCache.describe(copy) + '.';
                            return;
                        }
                    }

                    // El backend ya devuelve el texto traducido para el xofer; se muestra tal cual
                    this.decaError = error.error?.message ?? null;
                }
            }
        );
    }

    // Con conexión abre la URL pública; sin conexión descarga la copia guardada del PDF.
    async openDecaPdf(): Promise<void> {
        const result = await this.decaCache.openPdf(Number(this.idAlbara), this.decaUrl, 'DECA_' + this.infoAlbara.reference + '.pdf', this.decaFromBackup);

        if (result === 'none') {
            this.showAlert = true;
            this.messageAlert = 'Sin conexión y sin copia guardada de este DeCA.';
        } else if (result === 'backup') {
            this.decaFromBackup = true;

            const copy = await this.decaCache.getPdf(Number(this.idAlbara));
            if (copy) {
                this.decaBackupNotice = 'Sin conexión: copia del DeCA ' + this.dataCache.describe(copy) + '.';
            }
        }
    }

    downloadDeliveryNote(deliveryNoteId: string, token: string): void {
        // Construir la URL
        this.showLoader = true;
        const url = this.url + `deliveryNote/dmByDriver?id=${deliveryNoteId}&token=${token}`;
        // Hacer la petición GET al backend
        this.http.get(url, { responseType: 'blob' }).subscribe(
            (response: Blob) => {
                this.showLoader = false;
                // Si la respuesta es un archivo PDF (tipo blob), proceder con la descarga
                const fileURL = URL.createObjectURL(response);
                const a = document.createElement('a');
                a.href = fileURL;
                a.download = `Albaran_TRM_${deliveryNoteId}.pdf`; // O el nombre que desees
                a.click();
                URL.revokeObjectURL(fileURL); // Limpiar la URL objeto
            },
            (error: HttpErrorResponse) => {
                    this.showLoader = false;
                    this.showAlert = true;
                    this.messageAlert ='No se ha podido procesar tu solicitud'
            //this.showToast('No se ja podido procesar tu solicitud');

            }
        );
    }


    closeAlert(){
        this.showAlert = false;
    }
}
